let pickerApiLoaded = false;
let oauthToken = null;
let googleClientId = null;
let googleApiKey = null;
let googleAppId = null;
let dotNetHelper = null;

window.initializeGoogleDrivePicker = async (dotNetRef, clientId, apiKey, appId) => {
    dotNetHelper = dotNetRef;
    googleClientId = clientId;
    googleApiKey = apiKey;
    googleAppId = appId;

    console.log("Google Picker config from .NET", {
        clientId: googleClientId,
        apiKey: googleApiKey,
        appId: googleAppId
    });

    try {
        await loadGoogleScripts();
        gapi.load("auth", { callback: onAuthApiLoad });
        gapi.load("picker", { callback: onPickerApiLoad });
        console.log("Google Drive Picker initialized successfully");
    } catch (error) {
        console.error("Failed to initialize Google Drive Picker:", error);
        if (dotNetHelper) {
            dotNetHelper.invokeMethodAsync("HandlePickerError", error.message ?? "Failed to initialize Google Drive Picker");
        }
    }
};

function loadGoogleScripts() {
    return new Promise((resolve, reject) => {
        if (window.gapi && window.google && window.google.accounts) {
            resolve();
            return;
        }

        const gapiScript = document.createElement("script");
        gapiScript.src = "https://apis.google.com/js/api.js";
        gapiScript.async = true;
        gapiScript.defer = true;

        gapiScript.onload = () => {
            const gisScript = document.createElement("script");
            gisScript.src = "https://accounts.google.com/gsi/client";
            gisScript.async = true;
            gisScript.defer = true;

            gisScript.onload = () => resolve();
            gisScript.onerror = () => reject(new Error("Failed to load Google Identity Services script"));

            document.head.appendChild(gisScript);
        };

        gapiScript.onerror = () => reject(new Error("Failed to load Google API script"));
        document.head.appendChild(gapiScript);
    });
}

function onAuthApiLoad() {
    console.log("Google Auth API loaded");
}

function onPickerApiLoad() {
    pickerApiLoaded = true;
    console.log("Google Picker API loaded");
}

window.openGoogleDrivePicker = async (allowMultiple = true, allowFolders = false) => {
    if (!pickerApiLoaded) {
        const message = "Picker API not loaded";
        console.error(message);
        if (dotNetHelper) {
            dotNetHelper.invokeMethodAsync("HandlePickerError", message);
        }
        return;
    }

    if (!googleClientId) {
        const message = "Google client ID is missing. Check configuration.";
        console.error(message);
        if (dotNetHelper) {
            dotNetHelper.invokeMethodAsync("HandlePickerError", message);
        }
        return;
    }

    try {
        const tokenClient = google.accounts.oauth2.initTokenClient({
            client_id: googleClientId,
            scope: "https://www.googleapis.com/auth/drive.file",
            callback: tokenResponse => {
                if (tokenResponse.error) {
                    console.error("OAuth error:", tokenResponse.error);
                    if (dotNetHelper) {
                        dotNetHelper.invokeMethodAsync("HandlePickerError", tokenResponse.error);
                    }
                    return;
                }

                oauthToken = tokenResponse.access_token;
                createPicker(allowMultiple, allowFolders);
            }
        });

        tokenClient.requestAccessToken({ prompt: "consent" });
    } catch (error) {
        console.error("Failed to open Google Drive Picker:", error);
        if (dotNetHelper) {
            dotNetHelper.invokeMethodAsync("HandlePickerError", error.message ?? "Failed to open Google Drive Picker");
        }
    }
};

function createPicker(allowMultiple, allowFolders) {
    if (!oauthToken) {
        console.error("No OAuth token available");
        return;
    }

    if (!googleApiKey || !googleAppId) {
        const message = "Google API key or App ID is missing. Check configuration.";
        console.error(message);
        if (dotNetHelper) {
            dotNetHelper.invokeMethodAsync("HandlePickerError", message);
        }
        return;
    }

    try {
        let docsView = new google.picker.DocsView(google.picker.ViewId.DOCS)
            .setIncludeFolders(allowFolders)
            .setSelectFolderEnabled(allowFolders);

        if (!allowFolders) {
            docsView.setMimeTypes([
                "application/pdf",
                "image/jpeg",
                "image/jpg",
                "image/png",
                "application/msword",
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                "text/plain"
            ].join(","));
        }

        const picker = new google.picker.PickerBuilder()
            .addView(docsView)
            .setOAuthToken(oauthToken)
            .setDeveloperKey(googleApiKey)
            .setAppId(googleAppId)
            .setCallback(async data => {
                if (data.action === google.picker.Action.PICKED) {
                    const files = [];

                    for (const doc of data.docs) {
                        if (!allowFolders && doc.mimeType === "application/vnd.google-apps.folder") {
                            continue;
                        }

                        if (doc.mimeType === "application/vnd.google-apps.folder") {
                            const folderFiles = await fetchFilesFromFolder(doc.id, oauthToken);
                            files.push(...folderFiles);
                        } else {
                            files.push({
                                id: doc.id,
                                name: doc.name,
                                mimeType: doc.mimeType,
                                sizeBytes: doc.sizeBytes || 0,
                                downloadUrl: doc.url,
                                accessToken: oauthToken,
                                resourceKey: doc.resourceKey || null,
                                isFolder: false
                            });
                        }
                    }

                    if (dotNetHelper && files.length > 0) {
                        await dotNetHelper.invokeMethodAsync("HandleFilesSelected", files);
                    }
                } else if (data.action === google.picker.Action.CANCEL) {
                    console.log("User cancelled the picker");
                    if (dotNetHelper) {
                        await dotNetHelper.invokeMethodAsync("HandleFilesSelected", []);
                    }
                }
            })
            .setMaxItems(allowMultiple ? 100 : 1)
            .enableFeature(google.picker.Feature.MULTISELECT_ENABLED)
            .setTitle("Select files from Google Drive")
            .build();

        picker.setVisible(true);
    } catch (error) {
        console.error("Failed to create picker:", error);
        if (dotNetHelper) {
            dotNetHelper.invokeMethodAsync("HandlePickerError", error.message ?? "Failed to create picker");
        }
    }
}

async function fetchFilesFromFolder(folderId, accessToken) {
    const files = [];
    let pageToken = null;

    try {
        do {
            const url = new URL("https://www.googleapis.com/drive/v3/files");
            url.searchParams.append("q", "'" + folderId + "' in parents and trashed=false");
            url.searchParams.append("fields", "nextPageToken,files(id,name,mimeType,size,resourceKey)");
            url.searchParams.append("pageSize", "100");

            if (pageToken) {
                url.searchParams.append("pageToken", pageToken);
            }

            const response = await fetch(url.toString(), {
                headers: {
                    Authorization: "Bearer " + accessToken
                }
            });

            if (!response.ok) {
                throw new Error("Failed to fetch folder contents: " + response.statusText);
            }

            const data = await response.json();

            for (const file of data.files) {
                if (file.mimeType.startsWith("application/vnd.google-apps.")) {
                    if (file.mimeType === "application/vnd.google-apps.folder") {
                        const subfolderFiles = await fetchFilesFromFolder(file.id, accessToken);
                        files.push(...subfolderFiles);
                    }
                    continue;
                }

                const supportedMimeTypes = [
                    "application/pdf",
                    "image/jpeg",
                    "image/jpg",
                    "image/png",
                    "application/msword",
                    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                    "text/plain"
                ];

                if (supportedMimeTypes.includes(file.mimeType)) {
                    files.push({
                        id: file.id,
                        name: file.name,
                        mimeType: file.mimeType,
                        sizeBytes: parseInt(file.size) || 0,
                        downloadUrl: "https://www.googleapis.com/drive/v3/files/" + file.id + "?alt=media",
                        accessToken: accessToken,
                        resourceKey: file.resourceKey || null,
                        isFolder: false
                    });
                }
            }

            pageToken = data.nextPageToken;
        } while (pageToken);
    } catch (error) {
        console.error("Error fetching folder contents:", error);
    }

    return files;
}

window.cleanupGoogleDrivePicker = () => {
    oauthToken = null;
    dotNetHelper = null;
};